import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkGfm from 'remark-gfm'
import remarkDirective from 'remark-directive'
import remarkRehype from 'remark-rehype'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import rehypeAutolinkHeadings from 'rehype-autolink-headings'
import rehypeStringify from 'rehype-stringify'
import { visit } from 'unist-util-visit'
import { toString } from 'hast-util-to-string'

export type MarkdownHeading = {
  id: string
  text: string
  level: number
}

export type MarkdownResult = {
  markup: string
  headings: Array<MarkdownHeading>
}

// Convert directive nodes (:::callout, :::steps, :::step, :::card-grid, ::card)
// to HTML elements with data attributes for the React renderer to pick up.
function remarkDirectiveToHtml() {
  return (tree: any) => {
    visit(tree, (node: any) => {
      if (node.type !== 'containerDirective' && node.type !== 'leafDirective') return

      const data = node.data || (node.data = {})
      const attrs = node.attributes || {}

      switch (node.name) {
        case 'callout':
          data.hName = 'div'
          data.hProperties = {
            class: 'md-callout',
            'data-type': attrs.type || 'note',
          }
          break
        case 'steps':
          data.hName = 'div'
          data.hProperties = { class: 'md-steps' }
          break
        case 'step':
          data.hName = 'div'
          data.hProperties = {
            class: 'md-step',
            'data-n': String(attrs.n || '1'),
            'data-title': attrs.title || '',
          }
          break
        case 'card-grid':
          data.hName = 'div'
          data.hProperties = { class: 'md-card-grid' }
          break
        case 'card':
          data.hName = 'a'
          data.hProperties = {
            class: 'md-card',
            href: attrs.href || '#',
            'data-title': attrs.title || '',
            'data-description': attrs.description || '',
          }
          break
      }
    })
  }
}

export async function renderMarkdown(content: string): Promise<MarkdownResult> {
  const headings: Array<MarkdownHeading> = []

  const result = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkDirective)
    .use(remarkDirectiveToHtml)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeSlug)
    .use(rehypeAutolinkHeadings, {
      behavior: 'wrap',
      properties: { className: ['anchor'] },
    })
    .use(() => (tree) => {
      visit(tree, 'element', (node: any) => {
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.tagName)) {
          headings.push({
            id: node.properties?.id || '',
            text: toString(node),
            level: parseInt(node.tagName.charAt(1), 10),
          })
        }
      })
    })
    .use(rehypeStringify, { allowDangerousHtml: true })
    .process(content)

  return {
    markup: String(result),
    headings,
  }
}
